import React, { Component, PureComponent, Fragment } from 'react'
import { Map, List, fromJS } from 'immutable'
import { connect } from 'react-redux'
import { push } from 'react-router-redux'
import moment from 'moment'
import InterviewStatus from '../interview/status'
import NoteEditor from '../note/index'
import { InterviewEvents, InterviewApprovals, InterviewTasks } from '../interview/info'

export function CSMAggregationTable({ organizationView }) {
  const periodLabels = ['0-30', '30-60', '60-90']

  const rows = [
    {
      name: 'contactLoginEvents',
      label: 'Login events',
    },
    {
      name: 'embedViews',
      label: 'Embed views',
    },
    {
      name: 'surveyAnswers',
      label: 'Answers',
    },
    {
      name: 'publicSurveyAnswers',
      label: 'Public answers',
    },
    {
      name: 'publishedSurveyAnswers',
      label: 'Published answers',
    },
    {
      name: 'surveySends',
      label: 'Survey sends',
    },
    {
      name: 'surveyViews',
      label: 'Survey views',
    },
    {
      name: 'answerShares',
      label: 'Answer share',
    },
  ]

  if (!Map.isMap(organizationView) || organizationView.isEmpty() || !organizationView.get('organizationView'))
    return null

  return (
    <div className="organization-view-events">
      {rows.map((row, i) => (
        <div className="organization-view-event" key={i}>
          <div className="organization-view-event-label">{row.label}</div>
          <Events
            name={row.name}
            aggregations={organizationView.getIn(['organizationView', 'csmAggregations'])}
            periodLabels={periodLabels}
          />
        </div>
      ))}
    </div>
  )
}

function Events({ name, aggregations, periodLabels }) {
  let threshold = 0.1 // 5%
  let previous = false
  let trend = ''
  return aggregations
    .sort((a, b) => new Date(a.get('startDate')) - new Date(b.get('startDate')))
    .map(aggregation => {
      let value = parseInt(aggregation.get(name, 0))
      let growth = value / previous

      trend = 'grey'
      if (growth > 1 + threshold) trend = 'green'
      if (growth < 1 - threshold) trend = 'yellow'
      if (growth < 1 - threshold * 2) trend = 'tomato'
      if (previous === false) trend = 'grey'

      previous = value

      if (value > 1000) value = (value / 1000).toFixed(2) + 'k'

      if (value === 0) value = '-'

      return new Map({ trend, value, startDate: aggregation.get('startDate') })
    })
    .sort((a, b) => new Date(b.get('startDate')) - new Date(a.get('startDate')))
    .map((aggregation, i) => {
      return (
        <div
          key={aggregation.get('startDate')}
          className={`organization-view-event-tag tags-item--${aggregation.get('trend')}`}>
          <span className="organization-view-event-tag-label">{periodLabels[i]}</span>
          {aggregation.get('value')}
        </div>
      )
    })
}

function OrdersTable({ organizationView, pushLocation }) {
  const orderTypeLabels = {
    new_project: 'Uusi projekti',
    additional: 'Lisätyö',
  }

  const orderSourceLabels = {
    pipedrive: 'Pipedrive',
    platform: 'Alusta',
    client: 'Asiakasportaali',
  }

  const orderStatusLabels = {
    pending: 'Odottaa käsittelyä',
    processing: 'Käsittelyssä',
    processed: 'Käsitelty',
  }

  const columns = [
    {
      label: 'Nimi',
      name: 'name',
      render(row) {
        return (
          <button
            className="button-plain"
            onClick={e =>
              pushLocation(
                `/order?editId=${row.get('id')}&search=${row.get('name')}&page=1&sortBy=created_at&sortDir=desc`
              )
            }>
            {row.get('name')}
          </button>
        )
      },
    },
    {
      label: 'Status',
      name: 'status',
      render(row) {
        return orderStatusLabels[row.get('status')] || row.get('status')
      },
    },
    {
      label: 'Tyyppi',
      name: 'type',
      render(row) {
        return orderTypeLabels[row.get('type')] || row.get('type')
      },
    },
    {
      label: 'Lähde',
      name: 'source',
      render(row) {
        return orderSourceLabels[row.get('source')] || row.get('source')
      },
    },
    {
      label: 'Luotu',
      name: 'created_at',
      render(order) {
        return moment(order.get('created_at')).format('DD.MM.YYYY - HH.mm')
      },
    },
    {
      label: 'Päivitetty',
      name: 'updated_at',
      render(order) {
        return moment(order.get('updated_at')).format('DD.MM.YYYY - HH.mm')
      },
    },
  ]

  return (
    <Fragment>
      <table className="ui selectable table">
        <thead>
          <tr>
            {columns.map(column => (
              <th key={column.label}>{column.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {organizationView.getIn(['organizationView', 'orders']).map(order => (
            <tr key={order.get('id')}>
              {columns.map(column => (
                <td key={column.name}>{column.render ? column.render(order) : order.get(column.name)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Fragment>
  )
}

function ProjectsTable({ organizationView, pushLocation }) {
  const projectStatusText = {
    order: 'Tilaus',
    production: 'Tuotannossa',
    archived: 'Arkistoitu',
  }

  const projectColumns = [
    {
      label: 'Nimi',
      name: 'name',
      render(row) {
        return (
          <button className="button-plain" onClick={e => pushLocation(`/project/${row.get('id')}`)}>
            {row.get('name')}
          </button>
        )
      },
    },
    {
      label: 'Status',
      name: 'status',
      render(row) {
        return projectStatusText[row.get('status')] || row.get('status')
      },
    },
    {
      label: 'Info',
      name: 'info',
    },
    {
      label: 'Luotu',
      name: 'created_at',
      render(row) {
        return moment(row.get('created_at')).format('DD.MM.YYYY - HH.mm')
      },
    },
    {
      label: 'Päivitetty',
      name: 'updated_at',
      render(row) {
        return moment(row.get('updated_at')).format('DD.MM.YYYY - HH.mm')
      },
    },
  ]

  const interviewsColumns = [
    {
      label: 'Nimi',
      name: 'name',
      render(interview, project) {
        return (
          <button
            className="button-plain"
            onClick={e => pushLocation(`/project/${project.get('id')}/interview/${interview.get('id')}`)}>
            -- {interview.get('name')}
          </button>
        )
      },
    },
    {
      label: 'Status',
      name: 'status',
      render(i) {
        return <InterviewStatus interview={i} />
      },
    },
    {
      name: 'info',
      label: 'Info',
      width: 300,
      render(i) {
        return (
          <div className="coordination-status">
            <InterviewEvents interview={i} />
            <InterviewApprovals interview={i} />
            <InterviewTasks interview={i} />
          </div>
        )
      },
    },
    {
      label: 'Luotu',
      name: 'created_at',
      render(interview, project) {
        return moment(interview.get('created_at')).format('DD.MM.YYYY - HH.mm')
      },
    },
    {
      label: 'Päivitetty',
      name: 'updated_at',
      render(row) {
        return moment(row.get('updated_at')).format('DD.MM.YYYY - HH.mm')
      },
    },
  ]

  return (
    <Fragment>
      <table className="ui selectable table">
        <thead>
          <tr>
            {projectColumns.map(column => (
              <th key={column.label}>{column.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {organizationView.getIn(['organizationView', 'projects']).map(project => (
            <Fragment>
              <tr key={project.get('id')}>
                {projectColumns.map(column => (
                  <td key={column.name}>{column.render ? column.render(project) : project.get(column.name)}</td>
                ))}
              </tr>
              {project.get('interviews').map(interview => (
                <tr key={interview.get('id')}>
                  {interviewsColumns.map(column => (
                    <td key={column.name}>
                      {column.render ? column.render(interview, project) : project.get(column.name)}
                    </td>
                  ))}
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </Fragment>
  )
}

class OrganizationView extends Component {
  componentDidMount() {
    let { organizationView } = this.props
    if (organizationView.isEmpty()) return null
  }

  render() {
    let { organizationView, dispatch } = this.props
    if (organizationView.isEmpty() || !organizationView.get('organizationView')) return null

    let pushLocation = location => {
      dispatch({ type: 'ORGANIZATION_VIEW_CLOSE' })
      dispatch(push(location))
    }

    return (
      <div className="organization-view">
        <div className="organization-view-title">
          <h1>
            {organizationView.getIn(['organizationView', 'organization', 'name'])} (
            {organizationView.getIn(['organizationView', 'organization', 'businessid'])})
          </h1>
        </div>
        <div className="organization-view-body">
          <div className="organization-view-body-flexcontainer">
            <div className="organization-view-body-notepad">
              <div className="organization-view-body-item-title">Organisaation muistiinpanot</div>
              <div className="subeditor-area-content">
                <NoteEditor
                  entity="organization"
                  entityId={organizationView.getIn(['organizationView', 'organization', 'id'])}
                  type="production"
                />
              </div>
            </div>
          </div>
          <div className="organization-view-body-item">
            <div className="organization-view-body-item-title">Projektit ja haastattelut</div>
            <ProjectsTable organizationView={organizationView} pushLocation={pushLocation} />
          </div>
          <div className="organization-view-body-item">
            <div className="organization-view-body-item-title">Tilaukset</div>
            <OrdersTable organizationView={organizationView} pushLocation={pushLocation} />
          </div>
        </div>
        <div className="organization-view-footer">
          <button
            className="organization-view-button-cancel"
            onClick={e => dispatch({ type: 'ORGANIZATION_VIEW_CLOSE' })}>
            Sulje
          </button>
        </div>
      </div>
    )
  }
}

export default connect(state => ({ organizationView: state.organizationView }))(OrganizationView)
